// Transitions PR-1 — the itemized payoff quote. Read-only preview computed
// server-side from the same figures a payoff run settles; "Save snapshot"
// persists it (Transitions.Manage). A quote is non-binding and expressly not
// an event of default (§15.1A(i)); the BINDING statement is the one persisted
// when a Prepayment Notice is recorded.

import { useState } from "react";
import { Button, Label, Skeleton } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency } from "@/design-system/lib/format";
import {
  useIssuePayoffQuote,
  usePayoffQuoteHistory,
  usePayoffQuotePreview,
} from "@/hooks/portfolioAdmin/useLoanTransitions";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

const LINE_HELP: Array<{
  key: "outstandingPrincipal" | "accruedInterest" | "openArrears" | "prepaymentAdminFee" | "yieldMaintenancePremium";
  label: (pct: number) => string;
  help: string;
}> = [
  {
    key: "outstandingPrincipal",
    label: () => "Outstanding principal",
    help: "The remaining loan balance.",
  },
  {
    key: "accruedInterest",
    label: () => "Accrued interest",
    help: "Daily interest at the contract rate since the last monthly payment.",
  },
  {
    key: "openArrears",
    label: () => "Open arrears & fees",
    help: "Amounts already owed on the loan (unpaid servicing arrears, late/NSF fees).",
  },
  {
    key: "prepaymentAdminFee",
    label: (pct) => `Prepayment administration fee (${(pct * 100).toFixed(2)}%)`,
    help: "The note's prepayment administration fee on outstanding principal (§17.10).",
  },
  {
    key: "yieldMaintenancePremium",
    label: () => "Yield Maintenance Premium",
    help: "All remaining scheduled interest through the balloon at the contract rate — this IS the “remaining scheduled interest” figure, not a separate number (§17.10).",
  },
];

export function PayoffQuoteModal({
  loanId,
  canManage,
  open,
  onClose,
}: {
  loanId: string;
  canManage: boolean;
  open: boolean;
  onClose: () => void;
}) {
  const tz = useUserTimeZone();
  const today = isoDateInTz(new Date(), tz);
  const [asOf, setAsOf] = useState<string>(today);
  const preview = usePayoffQuotePreview(loanId, asOf || undefined, open);
  const history = usePayoffQuoteHistory(open ? loanId : undefined);
  const issue = useIssuePayoffQuote(loanId);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const quote = preview.data;

  const save = async () => {
    setError(null);
    setSaved(false);
    try {
      await issue.mutateAsync({ asOf: asOf || undefined });
      setSaved(true);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Payoff quote"
      description="Itemized figure to pay the loan off in full as of the chosen date. A quote is non-binding and can be requested anytime — requesting one is expressly not an event of default."
      size="lg"
    >
      <div className="flex flex-col gap-space-3" data-testid="payoff-quote-body">
        <div className="flex items-end gap-space-2">
          <div>
            <Label htmlFor="quote-as-of">As-of date</Label>
            <input
              id="quote-as-of"
              type="date"
              className="rounded border border-border-light bg-white-pure px-space-2 py-space-1 text-small"
              value={asOf}
              min={today}
              onChange={(e) => {
                setAsOf(e.target.value);
                setSaved(false);
              }}
              data-testid="quote-as-of-input"
            />
          </div>
          {canManage && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => void save()}
              disabled={issue.isPending || !quote}
              data-testid="save-quote-button"
            >
              {issue.isPending ? "Saving…" : "Save snapshot"}
            </Button>
          )}
        </div>

        {preview.isLoading && <Skeleton className="h-32 w-full" />}
        {preview.isError && (
          <p className="text-small text-status-error m-0">
            {extractErrorMessage(preview.error)}
          </p>
        )}

        {quote && (
          <table className="w-full text-small" data-testid="payoff-quote-table">
            <tbody>
              {LINE_HELP.map((line) => (
                <tr key={line.key} className="border-b border-border-light">
                  <td className="py-space-2 pr-space-2 align-top">
                    <span className="font-semibold">{line.label(quote.prepaymentAdminFeePct)}</span>
                    <p className="text-gray-mid m-0">{line.help}</p>
                  </td>
                  <td className="py-space-2 text-right align-top whitespace-nowrap">
                    {formatCurrency(quote[line.key])}
                  </td>
                </tr>
              ))}
              <tr>
                <td className="py-space-2 pr-space-2 font-semibold">Total payoff</td>
                <td
                  className="py-space-2 text-right font-semibold whitespace-nowrap"
                  data-testid="payoff-quote-total"
                >
                  {formatCurrency(quote.totalPayoff)}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {saved && (
          <p className="text-small text-status-success m-0" data-testid="quote-saved-note">
            Snapshot saved.
          </p>
        )}
        {error && <p className="text-small text-status-error m-0">{error}</p>}

        {(history.data?.length ?? 0) > 0 && (
          <div>
            <h4 className="text-small font-semibold m-0 mb-space-1">Saved quotes</h4>
            <ul className="m-0 p-0 list-none flex flex-col gap-space-1" data-testid="quote-history">
              {history.data!.map((q) => (
                <li key={q.id ?? q.createdAt ?? q.asOfDate} className="text-small text-gray-mid">
                  {q.asOfDate} — {formatCurrency(q.totalPayoff)}
                  {q.isBindingStatement ? " · binding statement" : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
